import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '@services/api';
import { useAuthStore } from '@store/authStore';
import { getFriendlyErrorMessage } from '@utils/friendlyError';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});
type LoginFormData = z.infer<typeof loginSchema>;
type LoginPayload = LoginFormData & { recaptchaToken?: string };

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-v3';

function getLoginErrorMessage(error: any) {
  const responseData = error?.response?.data;
  if (error?.response?.status === 401) {
    return responseData?.message || responseData?.error || 'Invalid email or password. Please check your credentials and try again.';
  }
  return getFriendlyErrorMessage(error, 'Unable to sign in. Please try again.');
}

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isIdleTimeout = searchParams.get('reason') === 'idle_timeout';

  const redirectTo = location.state?.from
    ? `${location.state.from.pathname}${location.state.from.search}${location.state.from.hash}`
    : '/dashboard';
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const {
    mutate: login,
    isPending,
    isError,
  } = useMutation({
    mutationFn: (data: LoginPayload) => authApi.login(data),
    onSuccess: ({ data: response }) => {
      const payload = response.data ?? response;
      if (!payload?.user || payload?.isOffline) {
        setLoginError(payload?.message || 'Authentication failed. Please check connection and try again.');
        return;
      }
      const { user, accessToken, refreshToken } = payload;
      setAuth(user, accessToken, refreshToken);
      if (payload.mustChangePassword || user.mustChangePassword) {
        navigate('/reset-password?forced=true', { replace: true });
        return;
      }
      navigate(redirectTo, { replace: true });
    },
    onError: (err: any) => {
      console.error('LOGIN ERROR:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
      }
      setLoginError(getLoginErrorMessage(err));
    },
  });

  const loadRecaptcha = () => {
    if (!RECAPTCHA_SITE_KEY) {
      return Promise.resolve();
    }

    if (window.grecaptcha) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('reCAPTCHA failed to load'));
      document.head.appendChild(script);
    });
  };

  const getRecaptchaToken = async () => {
    if (!RECAPTCHA_SITE_KEY) {
      return undefined;
    }

    await loadRecaptcha();

    return new Promise<string>((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: 'login' })
          .then(resolve)
          .catch(() => reject(new Error('reCAPTCHA verification failed')));
      });
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
      <p className="text-muted-foreground text-sm mb-6">Sign in to your account</p>

      {isIdleTimeout && (
        <div className="mb-5 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3.5 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2.5">
          <span className="text-base leading-none">⚠️</span>
          <span>Your session expired due to 30 minutes of inactivity. Please sign in to continue.</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit(async (data) => {
          setLoginError('');

          try {
            setIsVerifyingRecaptcha(true);
            const recaptchaToken = await getRecaptchaToken();
            login({ ...data, recaptchaToken });
          } catch (error) {
            setLoginError(error instanceof Error ? error.message : 'reCAPTCHA verification failed');
          } finally {
            setIsVerifyingRecaptcha(false);
          }
        })}
        className="space-y-4"
      >
        <div>
          <label className="block text-sm font-medium mb-1.5">Email</label>
          <input
            {...register('email')}
            type="email"
            placeholder="you@company.com"
            className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Password</label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-destructive text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        {(isError || loginError) && (
          <div className="bg-destructive/10 text-destructive text-sm px-3 py-2 rounded-lg">
            {loginError || 'Unable to sign in. Please try again.'}
          </div>
        )}

        <button
          type="submit"
          disabled={isPending || isVerifyingRecaptcha}
          className="w-full bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {isPending || isVerifyingRecaptcha ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        <a href="/forgot-password" className="text-primary hover:underline">
          Forgot password?
        </a>
      </p>
    </div>
  );
}
